// Lanzador de tutoriales del Búho-JASQ®.
// Muestra una lista de tutoriales paso a paso según el rol del usuario.
// Para estudiantes incluye un interruptor de VOZ del búho (Web Speech API).
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getTutorials, runTutorial } from "@/lib/tour";
import { getAgeTheme } from "@/lib/quizThemes";
import {
  owlVoiceSupported, isOwlVoiceOn, setOwlVoice, defaultOwlVoiceForStudents, speakOwl, stopOwl,
} from "@/lib/owlVoice";
import { OwlMascot } from "@/components/OwlMascot";

const ABSORB_MS = 700;

// autoClose: milisegundos tras los cuales el menú se «absorbe» hacia el botón 🦉
// del encabezado y se cierra solo (solo en el auto-inicio de sesión). Si la
// persona pasa el cursor, toca o enfoca el menú, el auto-cierre se cancela.
export default function TourLauncher({ open, onClose, autoClose = 0 }) {
  const modalRef = useRef(null);
  const cancelRef = useRef(false);
  const [absorb, setAbsorb] = useState(null); // {x, y} desplazamiento hacia el botón 🦉
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStudent = user?.role === "estudiante";
  const theme = getAgeTheme(user?.age);
  const isKid = isStudent && (theme.id === "kids" || theme.id === "teen");

  const [voiceOn, setVoiceOn] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isStudent) defaultOwlVoiceForStudents();
    setVoiceOn(isOwlVoiceOn());
  }, [open, isStudent]);

  const tutorials = getTutorials(user);

  useEffect(() => {
    setAbsorb(null);
    cancelRef.current = false;
    if (!open || !autoClose) return undefined;
    let closeT;
    const t = setTimeout(() => {
      if (cancelRef.current) return;
      const m = modalRef.current?.getBoundingClientRect();
      const b = document.querySelector('[data-testid="header-tour-btn"]')?.getBoundingClientRect();
      if (m && b) {
        setAbsorb({ x: b.left + b.width / 2 - (m.left + m.width / 2), y: b.top + b.height / 2 - (m.top + m.height / 2) });
      } else {
        setAbsorb({ x: 0, y: 0 });
      }
      closeT = setTimeout(() => onClose?.(), ABSORB_MS);
    }, autoClose);
    return () => { clearTimeout(t); clearTimeout(closeT); };
  }, [open, autoClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const keepOpen = () => { cancelRef.current = true; };

  const toggleVoice = () => {
    const next = setOwlVoice(!voiceOn);
    setVoiceOn(next);
    if (next) speakOwl("¡Hola! Soy Zarek y te voy a acompañar.", { force: true });
    else stopOwl();
  };

  const launch = (tut) => {
    onClose?.();
    // Pequeño delay para que el modal se cierre antes de iniciar el overlay del tour.
    setTimeout(() => {
      runTutorial(tut, { navigate, voiceEnabled: isStudent && voiceOn, kids: isKid });
    }, 180);
  };

  // Always render the Portal; AnimatePresence handles mount/unmount animations
  // so framer-motion cleans up the DOM correctly (prevents NotFoundError).
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="tour-overlay"
          className="fixed inset-0 z-[80] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: absorb ? 0 : 1 }} exit={{ opacity: 0 }}
          transition={absorb ? { duration: ABSORB_MS / 1000, ease: "easeIn" } : undefined}
          onClick={onClose}
          data-testid="tour-launcher-overlay"
        >
          <motion.div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            onPointerEnter={keepOpen}
            onPointerDown={keepOpen}
            onFocusCapture={keepOpen}
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={absorb
              ? { opacity: 0, scale: 0.04, x: absorb.x, y: absorb.y, rotate: 14, filter: "blur(6px)" }
              : { opacity: 1, scale: 1, y: 0, x: 0, rotate: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={absorb ? { duration: ABSORB_MS / 1000, ease: [0.55, 0, 0.9, 0.4] } : { type: "spring", stiffness: 240, damping: 22 }}
            className="w-full max-w-lg my-auto bg-white rounded-3xl shadow-2xl border-2 border-[#FFCC28] overflow-hidden max-h-[85vh] flex flex-col"
            data-testid="tour-launcher-modal"
          >
            {/* Cabecera */}
            <div className="relative shrink-0 px-5 pt-5 pb-4 bg-gradient-to-br from-[#FFF7DA] to-white border-b-2 border-[#F2E6B8]">
              <button
                onClick={onClose}
                data-testid="tour-launcher-close"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 border border-[#E1E5F0] flex items-center justify-center text-[#6E7591] hover:text-[#1F2547]"
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
              <div className="flex items-center gap-3">
                <OwlMascot size={64} message={isKid ? "¡Hola! ¿Qué aprendemos hoy? 🦉" : "Elige un tutorial paso a paso."} variant={isKid ? "party" : "hint"} testid="launcher-owl" />
              </div>
              <h2 className="font-display text-xl font-extrabold text-[#1F2547] mt-3">Tutoriales de Zarek</h2>
              <p className="text-sm text-[#6E7591]">Te guío paso a paso por cada módulo. Yo te llevo a la pantalla correcta. ✨</p>

              {isStudent && owlVoiceSupported() && (
                <button
                  onClick={toggleVoice}
                  data-testid="tour-voice-toggle"
                  className={`mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
                    voiceOn
                      ? "bg-[#A43790] text-white border-[#A43790]"
                      : "bg-white text-[#4C5478] border-[#E1E5F0] hover:border-[#A43790]"
                  }`}
                >
                  {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  Voz de Zarek: {voiceOn ? "Activada" : "Desactivada"}
                </button>
              )}
            </div>

            {/* Lista de tutoriales */}
            <div className="flex-1 min-h-0 p-4 space-y-2 overflow-y-auto" data-testid="tour-launcher-list">
              {tutorials.map((tut) => (
                <button
                  key={tut.id}
                  onClick={() => launch(tut)}
                  data-testid={`tour-item-${tut.id}`}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-2xl border-2 border-[#E1E5F0] hover:border-[#FFCC28] hover:bg-[#FFFCF0] transition-colors group"
                >
                  <span className={`shrink-0 grid place-items-center rounded-xl bg-[#FFF7DA] ${isKid ? "w-12 h-12 text-3xl" : "w-10 h-10 text-2xl"}`}>
                    {tut.emoji}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`block font-extrabold text-[#1F2547] ${isKid ? "text-lg" : "text-base"}`}>{tut.title}</span>
                    <span className="block text-xs text-[#6E7591] truncate">{tut.summary}</span>
                  </span>
                  <ChevronRight size={18} className="text-[#A6ACC1] group-hover:text-[#A43790] group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              ))}
              {tutorials.length === 0 && (
                <p className="text-center text-sm text-[#6E7591] py-6">No hay tutoriales disponibles para tu rol.</p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
